"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Crown,
  Shuffle,
  Check,
  Users,
  ArrowLeftRight,
  MoreHorizontal,
  UserPlus,
  UserMinus,
  Link2,
  Trash2,
  Trophy,
  Info,
  Sparkles,
  History,
  Search,
  Copy,
  RefreshCw,
  ArrowDownToLine,
  Replace as ReplaceIcon,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

import { Link } from "@/i18n/navigation";
import { useTournamentStore } from "@/entities/tournament";
import {
  arenaApi,
  type ArenaAssembly,
  type ArenaAssemblyMember,
  type ArenaAssemblyTeam,
} from "@/shared/api/arena";
import { userApi } from "@/shared/api/user";
import { api } from "@/shared/lib/api";
import { getOpggUrl } from "@/shared/lib/opgg";
import { useUserProfile } from "@/hooks/useAuth";
import { RankPill, CaptainStar } from "../team-card-bits";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/shared/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/shared/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/ui/tabs";
import { Input } from "@/shared/ui/inputs/input";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Badge } from "@/shared/ui/data-display/badge";
import { tournamentKeys } from "@/shared/api/query-keys";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";

type Phase = "preview" | "ongoing";

interface Props {
  tournamentId: string;
  isManager: boolean;
  lobbyCount: number;
  phase: Phase;
}

function errMsg(e: unknown, fallback: string): string {
  const m = (e as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;
  return m ?? fallback;
}

function MemberAvatar({ m, size = 30 }: { m: ArenaAssemblyMember; size?: number }) {
  return (
    <Avatar style={{ width: size, height: size }}>
      <AvatarImage src={m.avatarUrl ?? undefined} alt={m.displayName} />
      <AvatarFallback className="text-[10px]">
        {m.displayName.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

function ExternalTag() {
  const tUi = useTranslations("Ui");
  return (
    <Badge
      variant="info"
      className="px-1.5 py-0 text-[10px]"
      title={tUi("arenaRostersPanel.priglashenIzvneAkkauntMog")}
    >
      <Sparkles className="size-2.5" />
      {tUi("arenaRostersPanel.izvne")}
    </Badge>
  );
}

function SizeBadge({ n }: { n: number }) {
  if (n === 3) return <Badge variant="secondary">3/3</Badge>;
  if (n < 3) return <Badge variant="warning">{n}/3 · неполная</Badge>;
  return <Badge variant="info">{n}/3 · перебор</Badge>;
}

export function ArenaRostersPanel({ tournamentId, isManager, lobbyCount, phase }: Props) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const qc = useQueryClient();
  const assemblyKey = ["arena-assembly", tournamentId];
  const refreshTournament = useTournamentStore((s) => s.fetchTournamentById);
  const { data: me } = useUserProfile();
  const myUserId = me?.id ?? null;

  const [swapPick, setSwapPick] = useState<{ userId: string; rosterId: string } | null>(null);
  const [dialog, setDialog] = useState<
    | { type: "seat"; team: ArenaAssemblyTeam; replacing?: ArenaAssemblyMember }
    | { type: "remove"; team: ArenaAssemblyTeam; member: ArenaAssemblyMember }
    | { type: "invite"; team: ArenaAssemblyTeam }
    | { type: "disband"; team: ArenaAssemblyTeam }
    | null
  >(null);

  const { data, isLoading, error } = useQuery({
    queryKey: assemblyKey,
    queryFn: () => arenaApi.getAssembly(tournamentId),
  });
  const { data: leaderboard } = useQuery({
    queryKey: ["arena-leaderboard", tournamentId],
    queryFn: () => arenaApi.getLeaderboard(tournamentId),
  });
  const { data: audit } = useQuery({
    queryKey: ["arena-rosters-audit", tournamentId],
    queryFn: () => arenaApi.getRostersAudit(tournamentId),
    enabled: isManager,
  });

  // KAN-222: заявки на вход по ссылке — Pending-регистрации с целевой тройкой (teamId).
  // До одобрения игрок в тройке НЕ показан (KAN-219 интерим); организатор одобряет/отклоняет.
  const pendingKey = ["arena-pending-joiners", tournamentId];
  const { data: pendingJoiners } = useQuery({
    queryKey: pendingKey,
    enabled: isManager,
    queryFn: async () => {
      const res = await api.get(`/tournaments/${tournamentId}/registrations`);
      const regs = (res.data?.registrations ?? []) as Array<{
        id: string;
        userId?: string | null;
        teamId?: string | null;
        status: string;
        userDisplayName?: string;
        currentRank?: string;
      }>;
      return regs.filter(
        (r) => String(r.status).toLowerCase() === "pending" && !!r.teamId,
      );
    },
  });
  const pendingByRoster = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of pendingJoiners ?? [])
      if (r.teamId) map.set(r.teamId, (map.get(r.teamId) ?? 0) + 1);
    return map;
  }, [pendingJoiners]);
  const rosterNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of data?.teams ?? []) map.set(t.rosterId, t.name);
    return map;
  }, [data]);

  const approveJoinerM = useMutation({
    mutationFn: (regId: string) =>
      api.post(`/tournaments/${tournamentId}/registrations/${regId}/approve`),
    onSuccess: () => {
      toast.success(tToast("arenaRostersPanel.igrokOdobrenIDobavlen"));
      void qc.invalidateQueries({ queryKey: assemblyKey });
      void qc.invalidateQueries({ queryKey: pendingKey });
      // Рефреш «турнирных» источников (счётчики/гриды) — как в refreshParticipants ниже.
      void qc.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
      void refreshTournament(tournamentId);
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosOdobrit"))),
  });
  const rejectJoinerM = useMutation({
    mutationFn: (regId: string) =>
      api.post(`/tournaments/${tournamentId}/registrations/${regId}/reject`, {}),
    onSuccess: () => {
      toast.success(tToast("arenaRostersPanel.zayavkaOtklonena"));
      void qc.invalidateQueries({ queryKey: pendingKey });
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosOtklonit"))),
  });

  const pointsByRoster = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of leaderboard?.rows ?? []) map.set(r.rosterId, r.totalPoints);
    return map;
  }, [leaderboard]);
  const auditByRoster = useMemo(() => {
    const map = new Map<string, { who: string; what: string }>();
    for (const a of audit ?? []) map.set(a.rosterId, { who: a.who, what: a.what });
    return map;
  }, [audit]);

  const onData = (d: ArenaAssembly) => {
    qc.setQueryData(assemblyKey, d);
    setSwapPick(null);
    void qc.invalidateQueries({ queryKey: ["arena-leaderboard", tournamentId] });
    void qc.invalidateQueries({ queryKey: ["arena-rosters-audit", tournamentId] });
  };

  // KAN-147 #1: мутации, меняющие состав участников турнира (seat/replace/remove),
  // должны рефрешить и «турнирные» источники — иначе счётчики/гриды остаются stale.
  const refreshParticipants = () => {
    void qc.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
    void refreshTournament(tournamentId);
    try {
      window.dispatchEvent(
        new CustomEvent("tournament:participants:refresh", {
          detail: { tournamentId },
        }),
      );
    } catch {
      // ignore
    }
  };

  const assembleM = useMutation({
    mutationFn: () => arenaApi.assemble(tournamentId),
    onSuccess: (d) => {
      onData(d);
      void refreshTournament(tournamentId);
      toast.success(tToast("arenaRostersPanel.komandySobrany"));
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosSobratKomandy"))),
  });
  const confirmM = useMutation({
    mutationFn: () => arenaApi.confirmTeams(tournamentId),
    onSuccess: (d) => {
      onData(d);
      void refreshTournament(tournamentId);
      toast.success(tToast("arenaRostersPanel.sostavyPodtverzhdeny"));
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosPodtverditSostavy"))),
  });
  const swapM = useMutation({
    mutationFn: (v: { a: string; b: string }) => arenaApi.swapMembers(tournamentId, v.a, v.b),
    onSuccess: (d) => { onData(d); toast.success(tToast("arenaRostersPanel.igrokiPomenyanyMestami")); },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosPomenyatIgrokov"))),
  });
  const moveM = useMutation({
    mutationFn: (v: { rosterId: string; lobby: number }) =>
      arenaApi.moveTeamLobby(tournamentId, v.rosterId, v.lobby),
    onSuccess: onData,
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosPerenestiKomandu"))),
  });
  const captainM = useMutation({
    mutationFn: (v: { rosterId: string; userId: string }) =>
      arenaApi.setCaptain(tournamentId, v.rosterId, v.userId),
    onSuccess: (d) => { onData(d); toast.success(tToast("arenaRostersPanel.kapitanNaznachen")); },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosNaznachitKapitana"))),
  });
  const seatM = useMutation({
    mutationFn: (v: { rosterId: string; userId: string }) =>
      arenaApi.seatMember(tournamentId, v.rosterId, v.userId),
    onSuccess: (d) => { onData(d); refreshParticipants(); setDialog(null); toast.success(tToast("arenaRostersPanel.igrokPosazhenVKomandu")); },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosPosaditIgroka"))),
  });
  const replaceM = useMutation({
    mutationFn: (v: { rosterId: string; outUserId: string; inUserId: string }) =>
      arenaApi.replaceMember(tournamentId, v.rosterId, v.outUserId, v.inUserId),
    onSuccess: (d) => { onData(d); refreshParticipants(); setDialog(null); toast.success(tToast("arenaRostersPanel.igrokZamenen")); },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosZamenitIgroka"))),
  });
  const removeM = useMutation({
    mutationFn: (v: { rosterId: string; userId: string; mode: "bench" | "tournament" }) =>
      arenaApi.removeMember(tournamentId, v.rosterId, v.userId, v.mode),
    onSuccess: (d, v) => {
      onData(d);
      refreshParticipants();
      setDialog(null);
      toast.success(v.mode === "bench" ? tUi("litArenaRostersPanel.igrokOtpravlenVZapasnye") : tUi("litArenaRostersPanel.igrokUdalenIzTurnira"));
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosUbratIgroka"))),
  });
  const disbandM = useMutation({
    mutationFn: (rosterId: string) => arenaApi.disbandRoster(tournamentId, rosterId),
    onSuccess: (d) => { onData(d); setDialog(null); toast.success(tToast("arenaRostersPanel.komandaRasformirovana")); },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosRasformirovatKomandu"))),
  });

  const teamsByLobby = useMemo(() => {
    const map = new Map<number, ArenaAssembly["teams"]>();
    for (const t of data?.teams ?? []) {
      const arr = map.get(t.lobbyNumber) ?? [];
      arr.push(t);
      map.set(t.lobbyNumber, arr);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [data]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="rounded-xl">
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-2 pb-3">
              {[0, 1, 2].map((j) => (
                <div key={j} className="flex items-center gap-2">
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <Skeleton className="h-3.5 w-1/2" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-2xl border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
        {tUi("arenaRostersPanel.neUdalosZagruzitSostavy")}
      </div>
    );
  }

  const busy =
    assembleM.isPending || confirmM.isPending || swapM.isPending || moveM.isPending ||
    captainM.isPending || seatM.isPending || replaceM.isPending || removeM.isPending ||
    disbandM.isPending;

  // Команды ещё не собраны.
  if (data.state === "NotAssembled" || data.teams.length === 0) {
    return (
      <Card className="border-0 shadow-none rounded-2xl bg-black/6 dark:bg-white/4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              {tUi("arenaRostersPanel.uchastniki")}
              <Badge variant="secondary">{data.unassigned.length}</Badge>
            </span>
            {isManager && phase === "preview" ? (
              <Button size="sm" onClick={() => assembleM.mutate()} disabled={busy || data.unassigned.length < 3}>
                <Shuffle className="mr-2 h-4 w-4" />
                {assembleM.isPending ? tUi("litArenaRostersPanel.sobiraem") : tUi("litArenaRostersPanel.sobratKomandy")}
              </Button>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.unassigned.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{tUi("arenaRostersPanel.pokaNiktoNeZaregistrirovalsya")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
              {data.unassigned.map((m) => (
                <div key={m.userId} className="flex items-center gap-2 rounded-lg bg-background/60 px-2.5 py-1.5">
                  <MemberAvatar m={m} size={28} />
                  <Link
                    href={`/u/${m.userId}`}
                    className="min-w-0 flex-1 truncate text-sm hover:underline"
                  >
                    {m.displayName}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Клик ⇄ (preview swap by pick).
  const onSwapPick = (m: ArenaAssemblyMember, rosterId: string) => {
    if (!swapPick) { setSwapPick({ userId: m.userId, rosterId }); return; }
    if (swapPick.userId === m.userId) { setSwapPick(null); return; }
    if (swapPick.rosterId === rosterId) { setSwapPick({ userId: m.userId, rosterId }); return; }
    swapM.mutate({ a: swapPick.userId, b: m.userId });
  };

  return (
    <div className="space-y-4">
      {/* Шапка */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-black/6 px-4 py-3 dark:bg-white/4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{tUi("arenaRostersPanel.sostavy")}</span>
          {phase === "ongoing" ? (
            <Badge variant="live">{tUi("arenaRostersPanel.idetTurnir")}</Badge>
          ) : (
            <Badge variant={data.state === "Confirmed" ? "success" : "secondary"}>
              {data.state === "Confirmed" ? tUi("litArenaRostersPanel.podtverzhdeny") : tUi("litArenaRostersPanel.prevyuDoStarta")}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {data.teams.length} троек · {data.lobbyCount} лобби
          </span>
        </div>
        {isManager ? (
          phase === "preview" ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 text-xs ${swapPick ? "text-primary" : "text-muted-foreground"}`}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
                {swapPick ? tUi("litArenaRostersPanel.vyberiVtorogoIgroka") : tUi("litArenaRostersPanel.uDvuhIgrokovObmen")}
              </span>
              <Button variant="outline" size="sm" onClick={() => assembleM.mutate()} disabled={busy}>
                <Shuffle className="mr-2 h-4 w-4" />
                {tUi("arenaRostersPanel.peremeshatZanovo")}
              </Button>
              {data.state === "Preview" ? (
                <Button size="sm" onClick={() => confirmM.mutate()} disabled={busy}>
                  <Check className="mr-2 h-4 w-4" />
                  {tUi("arenaRostersPanel.podtverditSostavy")}
                </Button>
              ) : null}
            </div>
          ) : (
            <Badge variant="outline" className="text-xs">
              <Info className="size-3" />
              {tUi("arenaRostersPanel.peresborkaNedostupnaPosleStarta")}
            </Badge>
          )
        ) : null}
      </div>

      {/* KAN-222: заявки на вход по ссылке — ждут одобрения организатора */}
      {isManager && (pendingJoiners?.length ?? 0) > 0 ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <UserPlus className="size-4 text-amber-600 dark:text-amber-400" />
            {tUi("arenaRostersPanel.zayavkiPoSsylkeZhdut")}
            <Badge variant="secondary">{pendingJoiners?.length ?? 0}</Badge>
          </div>
          <div className="space-y-2">
            {(pendingJoiners ?? []).map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-background/60 px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{r.userDisplayName || r.userId || "—"}</span>
                  {r.currentRank ? (
                    <span className="text-xs text-muted-foreground">{r.currentRank}</span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">
                    → {r.teamId ? (rosterNameById.get(r.teamId) ?? tUi("litArenaRostersPanel.troyka")) : tUi("litArenaRostersPanel.troyka")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => approveJoinerM.mutate(r.id)}
                    disabled={approveJoinerM.isPending || rejectJoinerM.isPending}
                  >
                    <Check className="mr-1 size-3.5" /> {tUi("arenaRostersPanel.odobrit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => rejectJoinerM.mutate(r.id)}
                    disabled={approveJoinerM.isPending || rejectJoinerM.isPending}
                  >
                    <UserMinus className="mr-1 size-3.5" /> {tUi("arenaRostersPanel.otklonit")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Подсказка про очки (во время турнира) */}
      {phase === "ongoing" ? (
        <div className="flex items-center gap-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 text-sm">
          <Info className="size-4 shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="text-muted-foreground">
            <strong className="text-foreground">{tUi("arenaRostersPanel.ochkiPrivyazanyKKomande")}</strong> {tUi("arenaRostersPanel.zamenyIgrokovNeMenyayut")}
          </span>
        </div>
      ) : null}

      {/* Лобби с командами */}
      {teamsByLobby.map(([lobby, teams]) => (
        <div key={lobby} className="space-y-2">
          {data.lobbyCount > 1 ? (
            <div className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Лобби {lobby}</div>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {teams.map((t) => (
              <TeamCard
                key={t.rosterId}
                team={t}
                phase={phase}
                isManager={isManager}
                isMyTeam={!!myUserId && t.members.some((m) => m.userId === myUserId)}
                lobbyCount={lobbyCount}
                busy={busy}
                points={pointsByRoster.get(t.rosterId) ?? 0}
                pendingCount={pendingByRoster.get(t.rosterId) ?? 0}
                audit={auditByRoster.get(t.rosterId)}
                swapPickRosterId={swapPick?.rosterId ?? null}
                swapPickUserId={swapPick?.userId ?? null}
                onSwapPick={onSwapPick}
                onCaptain={(userId) => captainM.mutate({ rosterId: t.rosterId, userId })}
                onMoveLobby={(l) => moveM.mutate({ rosterId: t.rosterId, lobby: l })}
                onBench={(userId) => removeM.mutate({ rosterId: t.rosterId, userId, mode: "bench" })}
                onSeat={() => setDialog({ type: "seat", team: t })}
                onReplace={(member) => setDialog({ type: "seat", team: t, replacing: member })}
                onRemove={(member) => setDialog({ type: "remove", team: t, member })}
                onInvite={() => setDialog({ type: "invite", team: t })}
                onDisband={() => setDialog({ type: "disband", team: t })}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Скамейка запасных */}
      <div className="rounded-xl border border-dashed border-border p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <ArrowDownToLine className="size-4" />
          {tUi("arenaRostersPanel.skameykaZapasnyh")}
          <Badge variant="secondary">{data.unassigned.length}</Badge>
        </div>
        {data.unassigned.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">{tUi("arenaRostersPanel.skameykaPusta")}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.unassigned.map((m) => (
              <span key={m.userId} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs">
                <MemberAvatar m={m} size={20} />
                <Link href={`/u/${m.userId}`} className="hover:underline">
                  {m.displayName}
                </Link>
                {m.external ? <ExternalTag /> : null}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Диалоги */}
      {dialog?.type === "seat" ? (
        <SeatDialog
          team={dialog.team}
          replacing={dialog.replacing}
          bench={data.unassigned}
          busy={busy}
          tournamentId={tournamentId}
          onClose={() => setDialog(null)}
          onConfirm={(userId) => {
            if (dialog.replacing) {
              replaceM.mutate({ rosterId: dialog.team.rosterId, outUserId: dialog.replacing.userId, inUserId: userId });
            } else {
              seatM.mutate({ rosterId: dialog.team.rosterId, userId });
            }
          }}
        />
      ) : null}
      {dialog?.type === "remove" ? (
        <ConfirmRemoveDialog
          member={dialog.member}
          busy={busy}
          onClose={() => setDialog(null)}
          onBench={() => removeM.mutate({ rosterId: dialog.team.rosterId, userId: dialog.member.userId, mode: "bench" })}
          onRemove={() => removeM.mutate({ rosterId: dialog.team.rosterId, userId: dialog.member.userId, mode: "tournament" })}
        />
      ) : null}
      {dialog?.type === "invite" ? (
        <InviteDialog tournamentId={tournamentId} team={dialog.team} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.type === "disband" ? (
        <DisbandDialog
          team={dialog.team}
          points={pointsByRoster.get(dialog.team.rosterId) ?? 0}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={() => disbandM.mutate(dialog.team.rosterId)}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────── TeamCard ─────────────────────────── */

function TeamCard(props: {
  team: ArenaAssemblyTeam;
  phase: Phase;
  isManager: boolean;
  isMyTeam: boolean;
  lobbyCount: number;
  busy: boolean;
  points: number;
  pendingCount?: number;
  audit?: { who: string; what: string };
  swapPickRosterId: string | null;
  swapPickUserId: string | null;
  onSwapPick: (m: ArenaAssemblyMember, rosterId: string) => void;
  onCaptain: (userId: string) => void;
  onMoveLobby: (lobby: number) => void;
  onBench: (userId: string) => void;
  onSeat: () => void;
  onReplace: (m: ArenaAssemblyMember) => void;
  onRemove: (m: ArenaAssemblyMember) => void;
  onInvite: () => void;
  onDisband: () => void;
}) {
  const tCommon = useTranslations("Common");
  const tUi = useTranslations("Ui");
  const { team, phase, isManager, isMyTeam, lobbyCount, busy, points, audit } = props;
  const n = team.members.length;
  const empty = n === 0;
  const needsSub = n > 0 && n < 3;

  return (
    <Card className="relative overflow-hidden rounded-[14px]">
      {/* Градиент-акцент сверху — как у прочих карточек команд (макет). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(120%_180%_at_15%_0%,color-mix(in_srgb,var(--primary)_16%,transparent),transparent_70%)]" />
      <CardHeader className="relative pb-2">
        <CardTitle className="flex items-start gap-2 text-base">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-primary/35 to-muted font-heading text-xs font-bold shadow-[0_0_0_1px_color-mix(in_srgb,var(--primary)_30%,transparent)]">
            {team.tag}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate">{team.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{team.tag}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {empty ? <Badge variant="outline">0/3</Badge> : <SizeBadge n={n} />}
              {/* KAN-222: сколько игроков по ссылке ждут одобрения в эту тройку */}
              {props.pendingCount ? (
                <Badge variant="secondary" className="border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                  {props.pendingCount} ждёт одобрения
                </Badge>
              ) : null}
              <span className="inline-flex items-center rounded-lg border border-primary/30 px-1.5 text-[10px] font-bold uppercase leading-4 tracking-[0.05em] text-primary">
                {tUi("arenaRostersPanel.arenaTrio")}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Trophy className="size-3 text-yellow-500" />
                {points} {tCommon("pointsShort")}
              </span>
            </div>
          </div>
          {isMyTeam ? (
            <Button asChild variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs" title={tUi("arenaRostersPanel.otkrytStranitsuKomandy")}>
              <Link href={`/rosters/${team.rosterId}`}>
                {tUi("arenaRostersPanel.podrobnee")}
                <ChevronRight className="size-3.5" />
              </Link>
            </Button>
          ) : null}
          {isManager && phase === "preview" && lobbyCount > 1 ? (
            <Select value={String(team.lobbyNumber)} disabled={busy} onValueChange={(v) => props.onMoveLobby(Number(v))}>
              <SelectTrigger className="h-7 w-[62px] text-xs" title={tUi("arenaRostersPanel.lobbi")} aria-label={tUi("arenaRostersPanel.lobbi")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: lobbyCount }).map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>Л{i + 1}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {isManager ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-7 shrink-0" disabled={busy} aria-label={tUi("arenaRostersPanel.deystviyaKomandy")}>
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={props.onSeat}>
                  <UserPlus className="size-4" />
                  {phase === "ongoing" ? tUi("litArenaRostersPanel.posaditZamenu") : tUi("litArenaRostersPanel.posaditIgroka")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={props.onInvite}>
                  <Link2 className="size-4" />
                  {tUi("arenaRostersPanel.invaytSsylka")}
                </DropdownMenuItem>
                {empty ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onClick={props.onDisband}>
                      <Trash2 className="size-4" />
                      {tUi("arenaRostersPanel.rasformirovat")}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="relative space-y-1.5 pb-3">
        {empty ? (
          <div className="rounded-md border border-dashed p-3 text-center">
            <div className="text-sm text-muted-foreground">{tUi("arenaRostersPanel.vseIgrokiVybyli")}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {tUi("arenaRostersPanel.komandaBezSostavaNo")} <strong className="text-foreground">{points} {tCommon("pointsShort")}</strong>{" "}
              Очки можно перенести вручную в таблице перед расформированием.
            </div>
            {isManager ? (
              <div className="mt-2.5 flex justify-center gap-2">
                <Button size="sm" variant="outline" onClick={props.onSeat}>
                  <UserPlus className="mr-1 size-3.5" />{tUi("arenaRostersPanel.posadit")}
                </Button>
                <Button size="sm" variant="destructive" onClick={props.onDisband}>
                  <Trash2 className="mr-1 size-3.5" />{tUi("arenaRostersPanel.rasformirovat")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {team.members.map((m) => {
              const isSel = props.swapPickUserId === m.userId;
              const opggUrl =
                m.riotAccountName && m.riotAccountTag
                  ? getOpggUrl(m.riotAccountName, m.riotAccountTag, m.riotAccountRegion)
                  : null;
              return (
                <div
                  key={m.userId}
                  className={`relative flex flex-col items-center gap-1 rounded-lg border bg-background/60 px-2 py-2.5 text-center transition-colors ${isSel ? "bg-primary/10 ring-1 ring-primary" : ""}`}
                >
                  {isManager ? (
                    <div className="absolute right-1 top-1 flex items-center gap-0.5">
                      {phase === "preview" ? (
                        <button
                          type="button"
                          onClick={() => props.onSwapPick(m, team.rosterId)}
                          disabled={busy}
                          title={isSel ? tUi("litArenaRostersPanel.otmenitVybor") : tUi("litArenaRostersPanel.pomenyatMestami")}
                          aria-label={tUi("arenaRostersPanel.pomenyatIgrokaMestami")}
                          className={`rounded-md p-1 transition-colors ${isSel ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}
                        >
                          <ArrowLeftRight className="size-3.5" />
                        </button>
                      ) : null}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-6" disabled={busy} aria-label={tUi("arenaRostersPanel.deystviyaSIgrokom")}>
                            <MoreHorizontal className="size-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {!m.isCaptain ? (
                            <DropdownMenuItem onClick={() => props.onCaptain(m.userId)}>
                              <Crown className="size-4" />
                              {tUi("arenaRostersPanel.sdelatKapitanom")}
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => props.onReplace(m)}>
                            <ReplaceIcon className="size-4" />
                            {tUi("arenaRostersPanel.zamenit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => props.onBench(m.userId)}>
                            <ArrowDownToLine className="size-4" />
                            {tUi("arenaRostersPanel.vZapasnye")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => props.onRemove(m)}>
                            <UserMinus className="size-4" />
                            {tUi("arenaRostersPanel.udalitIzTurnira")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : null}

                  <div className="relative">
                    <MemberAvatar m={m} size={40} />
                    {m.isCaptain ? (
                      <span className="absolute -bottom-1 -right-1 rounded-full bg-card p-0.5 shadow-sm" title={tUi("arenaRostersPanel.kapitan")}>
                        <CaptainStar className="size-3 text-primary" />
                      </span>
                    ) : null}
                  </div>

                  <Link
                    href={`/u/${m.userId}`}
                    className="max-w-full truncate text-[13px] font-semibold leading-tight hover:text-primary"
                  >
                    {m.displayName}
                  </Link>

                  {m.riotAccountName && m.riotAccountTag ? (
                    opggUrl ? (
                      <a
                        href={opggUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="max-w-full truncate text-[10px] tabular-nums text-muted-foreground hover:text-foreground"
                      >
                        {m.riotAccountName}#{m.riotAccountTag}
                      </a>
                    ) : (
                      <span className="max-w-full truncate text-[10px] tabular-nums text-muted-foreground">
                        {m.riotAccountName}#{m.riotAccountTag}
                      </span>
                    )
                  ) : null}

                  <RankPill rank={m.currentRank} />
                  {m.external ? <ExternalTag /> : null}
                </div>
              );
            })}
          </div>
        )}

        {needsSub && isManager ? (
          <div className="flex items-center gap-2 rounded-md bg-yellow-500/10 px-2.5 py-1.5 text-xs text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="size-3.5" />
            Не хватает {3 - n} игрока — посадите из запасных или по ссылке.
          </div>
        ) : null}

        {isManager && audit ? (
          <div className="mt-2 flex items-center gap-1.5 border-t border-dashed pt-2 text-[11px] text-muted-foreground">
            <History className="size-3" />
            <span className="truncate">
              <strong className="font-medium text-foreground">{audit.who}</strong> · {audit.what}
            </span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Dialogs ─────────────────────────── */

function SeatDialog(props: {
  team: ArenaAssemblyTeam;
  replacing?: ArenaAssemblyMember;
  bench: ArenaAssemblyMember[];
  busy: boolean;
  tournamentId: string;
  onClose: () => void;
  onConfirm: (userId: string) => void;
}) {
  const tUi = useTranslations("Ui");
  const { team, replacing, bench, busy } = props;
  const [tab, setTab] = useState<"bench" | "site" | "invite">(bench.length ? "bench" : "site");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string | null>(null);

  const { data: siteResults, isFetching } = useQuery({
    queryKey: ["user-search", q],
    queryFn: () => userApi.searchUsers(q),
    enabled: tab === "site" && q.trim().length >= 2,
  });

  const title = replacing ? tUi("litArenaRostersPanel.zamenitName", { name: replacing.displayName }) : tUi("litArenaRostersPanel.posaditIgroka2");

  return (
    <Dialog open onOpenChange={(o) => { if (!o) props.onClose(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Команда {team.name} · {team.members.length}/3</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => { setTab(v as typeof tab); setPicked(null); }}>
          <TabsList className="w-full">
            <TabsTrigger value="bench" className="flex-1">{tUi("arenaRostersPanel.izZapasnyh")}</TabsTrigger>
            <TabsTrigger value="site" className="flex-1">{tUi("arenaRostersPanel.poiskPoSaytu")}</TabsTrigger>
            <TabsTrigger value="invite" className="flex-1">{tUi("arenaRostersPanel.poSsylke")}</TabsTrigger>
          </TabsList>

          <TabsContent value="bench" className="mt-3">
            {bench.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{tUi("arenaRostersPanel.skameykaPustaIspolzuytePoisk")}</p>
            ) : (
              <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto">
                {bench.map((u) => (
                  <PickRow key={u.userId} id={u.userId} name={u.displayName} avatarUrl={u.avatarUrl} external={u.external} picked={picked === u.userId} onPick={() => setPicked(u.userId)} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="site" className="mt-3 space-y-2">
            <div className="flex items-center gap-2 rounded-md border px-3 h-9">
              <Search className="size-4 text-muted-foreground" />
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus -- главное поле диалога замены: поиск по сайту
                autoFocus
                aria-label={tUi("arenaRostersPanel.nikPolzovatelyaSayta")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tUi("arenaRostersPanel.nikLyubogoPolzovatelyaSayta")}
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
            <div className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
              {q.trim().length < 2 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{tUi("arenaRostersPanel.vvediteMinimum2Simvola")}</p>
              ) : isFetching ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{tUi("arenaRostersPanel.poisk")}</p>
              ) : (siteResults?.length ?? 0) === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{tUi("arenaRostersPanel.nikogoNeNaydeno")}</p>
              ) : (
                siteResults!.map((u) => (
                  <PickRow key={u.id} id={u.id} name={u.displayName} avatarUrl={u.avatarUrl} sub={`@${u.username}`} picked={picked === u.id} onPick={() => setPicked(u.id)} />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="invite" className="mt-3">
            <div className="rounded-md border p-3 text-sm text-muted-foreground">
              {tUi("arenaRostersPanel.otpravteIgrokuInvaytSsylku")}
            </div>
          </TabsContent>
        </Tabs>

        {tab !== "invite" ? (
          <DialogFooterRow>
            <Button variant="outline" onClick={props.onClose} disabled={busy}>{tUi("arenaRostersPanel.otmena")}</Button>
            <Button disabled={!picked || busy} onClick={() => picked && props.onConfirm(picked)}>
              <Check className="mr-1 size-4" />
              {replacing ? tUi("litArenaRostersPanel.zamenitName") : tUi("litArenaRostersPanel.posadit")}
            </Button>
          </DialogFooterRow>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function PickRow(props: {
  id: string; name: string; avatarUrl?: string | null; sub?: string; external?: boolean;
  picked: boolean; onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onPick}
      className={`flex items-center gap-2.5 rounded-md border p-2 text-left transition-colors ${props.picked ? "border-primary bg-primary/10" : "hover:bg-accent"}`}
    >
      <Avatar className="size-7">
        <AvatarImage src={props.avatarUrl ?? undefined} alt={props.name} />
        <AvatarFallback className="text-[10px]">{props.name.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{props.name}</span>
          {props.external ? <ExternalTag /> : null}
        </div>
        {props.sub ? <div className="text-[11px] text-muted-foreground">{props.sub}</div> : null}
      </div>
      {props.picked ? <Check className="size-4 text-primary" /> : null}
    </button>
  );
}

function DialogFooterRow({ children }: { children: React.ReactNode }) {
  return <div className="mt-1 flex justify-end gap-2">{children}</div>;
}

function ConfirmRemoveDialog(props: {
  member: ArenaAssemblyMember;
  busy: boolean;
  onClose: () => void;
  onBench: () => void;
  onRemove: () => void;
}) {
  const tUi = useTranslations("Ui");
  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) props.onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{tUi("arenaRostersPanel.udalitIzTurnira2")}</AlertDialogTitle>
          <AlertDialogDescription>
            <strong className="text-foreground">{props.member.displayName}</strong> {tUi("arenaRostersPanel.budetPolnostyuIsklyuchenIz")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={props.onBench} disabled={props.busy}>
            <ArrowDownToLine className="mr-1 size-4" />{tUi("arenaRostersPanel.vZapasnye")}
          </Button>
          <div className="flex gap-2">
            <AlertDialogCancel disabled={props.busy}>{tUi("arenaRostersPanel.otmena")}</AlertDialogCancel>
            <Button variant="destructive" onClick={props.onRemove} disabled={props.busy}>
              <Trash2 className="mr-1 size-4" />{tUi("arenaRostersPanel.udalit")}
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DisbandDialog(props: {
  team: ArenaAssemblyTeam;
  points: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const tCommon = useTranslations("Common");
  const tUi = useTranslations("Ui");
  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) props.onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{tUi("arenaRostersPanel.rasformirovatKomandu")}</AlertDialogTitle>
          <AlertDialogDescription>
            «{props.team.name}» будет удалена из турнира. За ней закреплено{" "}
            <strong className="text-foreground">{props.points} {tCommon("pointsShort")}</strong> {tUi("arenaRostersPanel.oniIscheznutIzLiderborda")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={props.busy}>{tUi("arenaRostersPanel.otmena")}</AlertDialogCancel>
          <Button variant="destructive" onClick={props.onConfirm} disabled={props.busy}>
            <Trash2 className="mr-1 size-4" />{tUi("arenaRostersPanel.rasformirovat")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function InviteDialog(props: { tournamentId: string; team: ArenaAssemblyTeam; onClose: () => void }) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["arena-roster-invite", props.tournamentId, props.team.rosterId],
    queryFn: () => arenaApi.getRosterInvite(props.tournamentId, props.team.rosterId),
  });
  const reissueM = useMutation({
    mutationFn: () => arenaApi.reissueRosterInvite(props.tournamentId, props.team.rosterId),
    onSuccess: (d) => {
      qc.setQueryData(["arena-roster-invite", props.tournamentId, props.team.rosterId], d);
      toast.success(tToast("arenaRostersPanel.ssylkaPerevypuschenaStarayaBolshe"));
    },
    onError: (e) => toast.error(errMsg(e, tUi("litArenaRostersPanel.neUdalosPerevypustitSsylku"))),
  });

  const url = data ? `${typeof window !== "undefined" ? window.location.origin : ""}${data.invitePath}` : "";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) props.onClose(); }}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{tUi("arenaRostersPanel.invaytSsylkaVTroyku")}</DialogTitle>
          <DialogDescription>Команда {props.team.name}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {tUi("arenaRostersPanel.lyuboyKtoPereydetPo")}
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={isLoading ? tUi("litArenaRostersPanel.zagruzka") : url} className="font-mono text-xs" />
          <Button
            variant="outline"
            size="icon"
            disabled={!url}
            onClick={() => { void navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
            title={tUi("arenaRostersPanel.kopirovat")}
            aria-label={tUi("arenaRostersPanel.kopirovatSsylku")}
          >
            {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={reissueM.isPending}
            onClick={() => reissueM.mutate()}
            title={tUi("arenaRostersPanel.perevypustitSsylku")}
            aria-label={tUi("arenaRostersPanel.perevypustitSsylku")}
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
